// src/containers/preview/DemoClassPopup.js
//
// "Meet the class" — what a teacher's View Students screen looks like, with a
// made-up class. Tap a student to see their topic progress, scores and every
// question they answered (right / wrong / what they picked). The questions are
// the demo topics' REAL game questions; the students and answers are made up
// (demoContent.js → studentReport).

import React, { useEffect, useMemo, useState } from "react";
import ModalPortal from "../../components/common/ModalPortal";
import AvatarWithOutfit from "../../components/avatar/AvatarWithOutfit";
import { DEMO_CLASS, DEMO_TEACHER, studentReport } from "./demoContent";
import { trackDemo } from "../../services/demoTracker";

const NO_ITEMS = {};

const Badge = ({ on, icon, label = "" }) => (
  <span className={`pvd-badge ${on ? "is-done" : "is-not"}`}>
    {icon} {label || (on ? "Done" : "Not started")}
  </span>
);

const DemoClassPopup = ({ topics, loading, onClose, onSignup }) => {
  const [openId, setOpenId] = useState("");
  const [openTopic, setOpenTopic] = useState("");

  const reports = useMemo(() => Object.fromEntries(DEMO_CLASS.map((s) => [s.id, studentReport(s, topics)])), [topics]);
  const student = DEMO_CLASS.find((s) => s.id === openId) || null;
  const report = student ? reports[student.id] : null;
  const hasSing = topics.some((t) => t.singAlongUrl);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (openId ? setOpenId("") : onClose?.());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, openId]);

  const open = (s) => {
    setOpenId(s.id);
    setOpenTopic("");
    trackDemo("student", { slug: s.id, title: s.first });
  };

  return (
    <ModalPortal>
      <div className="pvd-pop" role="dialog" aria-modal="true" aria-label="Sample class" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="pvd-box is-wide">
          <header className="pvd-head">
            <h3>
              👩‍🏫 {DEMO_TEACHER.name}'s class <span>· sample students</span>
            </h3>
            <button type="button" className="pvd-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <p className="pvd-banner">
            🔎 This is the teacher's <b>View Students</b> screen with a made-up class. In your portal it fills in by itself as your students watch, sing and play.
          </p>

          {loading ? (
            <p className="pvd-empty">Loading the class…</p>
          ) : !student ? (
            <>
              <p className="pvd-sub">Tap a student to see their scores and every answer.</p>
              <div className="pvd-class-grid">
                {DEMO_CLASS.map((s) => {
                  const r = reports[s.id];
                  return (
                    <button key={s.id} type="button" className="pvd-kid" onClick={() => open(s)}>
                      <AvatarWithOutfit look={s.look} size={84} items={NO_ITEMS} />
                      <strong>
                        {s.first} {s.last}
                      </strong>
                      <span className="pvd-kid-stats">
                        <span>🎬 {r.videoComplete}/{r.totalTopics}</span>
                        <span>🎵 {r.musicComplete}/{r.totalTopics}</span>
                        <span>🎮 {r.gameComplete}/{r.totalTopics}</span>
                        <span>⭐ {r.totalScore}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="pvd-kid-detail">
              <button type="button" className="pvd-back" onClick={() => setOpenId("")}>
                ← Back to the class
              </button>

              <div className="pvd-kid-head">
                <AvatarWithOutfit look={student.look} size={110} items={NO_ITEMS} />
                <div>
                  <h4>
                    {student.first} {student.last}
                  </h4>
                  <p>🪙 {student.coins} coins this week</p>
                </div>
              </div>

              <div className="pvd-sum">
                <div>
                  <b>🎬 Videos</b>
                  <span>
                    {report.videoComplete}/{report.totalTopics}
                  </span>
                </div>
                <div>
                  <b>🎵 Songs</b>
                  <span>
                    {report.musicComplete}/{report.totalTopics}
                  </span>
                </div>
                <div>
                  <b>🎮 Games</b>
                  <span>
                    {report.gameComplete}/{report.totalTopics}
                  </span>
                </div>
                <div>
                  <b>⭐ Score</b>
                  <span>{report.totalScore}</span>
                </div>
              </div>

              <div className="pvd-rows">
                {report.rows.map((row) => (
                  <div key={row.slug} className="pvd-row">
                    <div className="pvd-row-top">
                      <div>
                        <h5>{row.title}</h5>
                        {row.standardNumber && <span className="sp-pill">{row.standardNumber}</span>}
                      </div>
                      <div className="pvd-badges">
                        <Badge on={row.video} icon="🎬" />
                        <Badge on={row.music} icon="🎵" />
                        {hasSing && <Badge on={row.singalong} icon="🎤" />}
                        <Badge on={row.game} icon="🎮" label={row.game ? `${row.right}/${row.total} right` : ""} />
                        <span className="pvd-badge is-score">⭐ {row.score}</span>
                      </div>
                    </div>

                    {row.game && (
                      <>
                        <p className="pvd-row-meta">
                          Game {row.quizScore} + bonus {row.bonusScore} · played {row.plays} {row.plays === 1 ? "time" : "times"}
                        </p>
                        <button type="button" className="pvd-link" onClick={() => setOpenTopic((v) => (v === row.slug ? "" : row.slug))}>
                          {openTopic === row.slug ? "Hide answers ▲" : "See questions & answers ▼"}
                        </button>
                        {openTopic === row.slug && (
                          <ol className="pvd-qa">
                            {row.answers.map((a, i) => (
                              <li key={i} className={a.correct ? "is-right" : "is-wrong"}>
                                <span className="pvd-qa-q">{a.question}</span>
                                <span className="pvd-qa-a">
                                  {a.correct ? "✓" : "✗"} {a.selectedText}
                                  {!a.correct && <em> — correct: {a.correctText}</em>}
                                </span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pvd-box-foot">
            <button type="button" className="pvd-cta" onClick={onSignup}>
              ✨ Get this for my class
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default DemoClassPopup;
